import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  SharepointColumnInfo,
  SharepointLibraryInfo,
  UserFeedbackConfigDto,
  BreaktimeAnnouncementConfig,
  BreaktimeAnnouncementConfigSummary,
  HelpLinkDto,
} from './types';
import {
  getUserFeedbackConfig,
  createUserFeedbackConfig,
  updateUserFeedbackConfig,
  deleteUserFeedbackConfig,
  getSharepointColumns,
  getSharepointLibraries,
  getBreaktimeAnnouncementSummaries,
  getBreaktimeAnnouncementConfig,
  upsertBreaktimeAnnouncementConfig,
  deleteBreaktimeAnnouncementConfig,
  getHelpLinks,
  createHelpLink,
  updateHelpLink,
  deleteHelpLink,
} from './api';
import { QueryKeys } from '../../api/queryKeys';
import { useInvalidatingMutation } from '../../api/useInvalidatingMutation';

/**
 * Hook to fetch user feedback configurations.
 * @returns Query result containing an array of UserFeedbackConfigDto.
 */
export const useUserFeedbackConfigQuery = () => {
  return useQuery<UserFeedbackConfigDto[]>({
    queryKey: [QueryKeys.USER_FEEDBACK_CONFIG],
    queryFn: getUserFeedbackConfig,
  });
};

/**
 * Hook to create a new user feedback configuration.
 * On success, invalidates the user feedback configuration query.
 */
export const useCreateUserFeedbackConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createUserFeedbackConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.USER_FEEDBACK_CONFIG],
      });
    },
  });
};

/**
 * Hook to update an existing user feedback configuration.
 * On success, invalidates the user feedback configuration query.
 */
export const useUpdateUserFeedbackConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUserFeedbackConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.USER_FEEDBACK_CONFIG],
      });
    },
  });
};

/**
 * Hook to delete a user feedback configuration.
 * On success, invalidates the user feedback configuration query.
 */
export const useDeleteUserFeedbackConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteUserFeedbackConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.USER_FEEDBACK_CONFIG],
      });
    },
  });
};

/**
 * Hook to fetch SharePoint libraries for docs-link configuration.
 */
export const useSharepointLibrariesQuery = (enabled: boolean = true) => {
  return useQuery<SharepointLibraryInfo[]>({
    queryKey: [QueryKeys.SHAREPOINT_LIBRARIES],
    queryFn: getSharepointLibraries,
    enabled,
  });
};

/**
 * Hook to fetch SharePoint columns for a selected library.
 */
export const useSharepointColumnsQuery = (
  listName: string | undefined,
  enabled: boolean = true,
) => {
  return useQuery<SharepointColumnInfo[]>({
    queryKey: [QueryKeys.SHAREPOINT_COLUMNS, listName],
    queryFn: () => getSharepointColumns(listName!),
    enabled: enabled && !!listName,
  });
};

/**
 * Hook to fetch breaktime announcement summaries for all inventory centers.
 */
export const useBreaktimeAnnouncementSummariesQuery = () => {
  return useQuery<BreaktimeAnnouncementConfigSummary[]>({
    queryKey: [QueryKeys.BREAKTIME_ANNOUNCEMENT_CONFIG_SUMMARIES],
    queryFn: getBreaktimeAnnouncementSummaries,
  });
};

/**
 * Hook to fetch breaktime announcement configuration detail for one inventory center.
 */
export const useBreaktimeAnnouncementConfigQuery = (
  inventoryCenterId: string | undefined,
  enabled: boolean = true,
) => {
  return useQuery<BreaktimeAnnouncementConfig | null>({
    queryKey: [QueryKeys.BREAKTIME_ANNOUNCEMENT_CONFIG, inventoryCenterId],
    queryFn: () => getBreaktimeAnnouncementConfig(inventoryCenterId!),
    enabled: enabled && !!inventoryCenterId,
    retry: false,
  });
};

/**
 * Hook to upsert breaktime announcement configuration.
 */
export const useUpsertBreaktimeAnnouncementConfigMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: upsertBreaktimeAnnouncementConfig,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.BREAKTIME_ANNOUNCEMENT_CONFIG_SUMMARIES],
      });
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.BREAKTIME_ANNOUNCEMENT_CONFIG,
          variables.inventoryCenterId,
        ],
      });
    },
  });
};

/**
 * Hook to delete breaktime announcement configuration.
 */
export const useDeleteBreaktimeAnnouncementConfigMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteBreaktimeAnnouncementConfig,
    onSuccess: (_, inventoryCenterId) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.BREAKTIME_ANNOUNCEMENT_CONFIG_SUMMARIES],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.BREAKTIME_ANNOUNCEMENT_CONFIG, inventoryCenterId],
      });
    },
  });
};

/**
 * Hook to fetch the help links shown on each page.
 */
export const useHelpLinksQuery = () => {
  return useQuery<HelpLinkDto[]>({
    queryKey: [QueryKeys.HELP_LINKS],
    queryFn: getHelpLinks,
    meta: { errorMessageKey: 'errorFetchingHelpLinks' },
  });
};

/**
 * Hook to create a help link.
 */
export const useCreateHelpLinkMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.HELP_LINKS],
    createHelpLink,
    'errorCreatingHelpLink',
  );

/**
 * Hook to update a help link.
 */
export const useUpdateHelpLinkMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.HELP_LINKS],
    updateHelpLink,
    'errorUpdatingHelpLink',
  );

/**
 * Hook to delete a help link.
 */
export const useDeleteHelpLinkMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.HELP_LINKS],
    deleteHelpLink,
    'errorDeletingHelpLink',
  );
